import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  $getSelection,
  $isRangeSelection,
  COMMAND_PRIORITY_CRITICAL,
  COMMAND_PRIORITY_HIGH,
  COMMAND_PRIORITY_LOW,
  KEY_ESCAPE_COMMAND,
  SELECTION_CHANGE_COMMAND,
} from 'lexical';
import { $isLinkNode, TOGGLE_LINK_COMMAND, $isAutoLinkNode, $createLinkNode } from '@lexical/link';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { mergeRegister } from '@lexical/utils';
import { getSelectedNode } from '../../utils/getSelectedNode';
import { sanitizeUrl } from '../../utils/url';
import { Check, Close, Edit, Delete } from '@mui/icons-material';
import { Tooltip, IconButton } from '@mui/material';

function positionEditorElement(editorElem, rect, anchorElem) {
  if (rect === null) {
    editorElem.style.opacity = '0';
    editorElem.style.transform = 'translate(-10000px, -10000px)';
    return;
  }

  const floatingElemRect = editorElem.getBoundingClientRect();
  const anchorElementRect = anchorElem.getBoundingClientRect();
  const scrollerElem = anchorElem.parentElement;
  const editorScrollerRect = scrollerElem
    ? scrollerElem.getBoundingClientRect()
    : anchorElementRect;

  let top = rect.top - floatingElemRect.height - 8 - anchorElementRect.top;
  let left = rect.left - anchorElementRect.left + (rect.width - floatingElemRect.width) / 2;

  if (top < editorScrollerRect.top - anchorElementRect.top + 8) {
    top += floatingElemRect.height + rect.height + 16;
  }

  if (left < 0) left = 4;

  editorElem.style.opacity = '1';
  editorElem.style.transform = `translate(${left}px, ${top}px)`;
}

function FloatingLinkEditor({
  editor,
  isLink,
  setIsLink,
  anchorElem,
  isLinkEditMode,
  setIsLinkEditMode,
}) {
  const editorRef = useRef(null);
  const inputRef = useRef(null);
  const [linkUrl, setLinkUrl] = useState('');
  const [editedLinkUrl, setEditedLinkUrl] = useState('');
  const [lastSelection, setLastSelection] = useState(null);

  const $updateLinkEditor = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      const node = getSelectedNode(selection);
      const linkParent = $isLinkNode(node.getParent()) ? node.getParent() : null;
      const linkNode = $isLinkNode(node) ? node : linkParent;

      if (linkNode) {
        setLinkUrl(linkNode.getURL());
      } else if (!$isLinkNode(node)) {
        setLinkUrl('');
      }

      const editorElem = editorRef.current;
      const nativeSelection = window.getSelection();
      const activeElement = document.activeElement;

      if (editorElem === null) return;

      const rootElement = editor.getRootElement();
      if (
        selection !== null &&
        nativeSelection !== null &&
        rootElement !== null &&
        rootElement.contains(nativeSelection.anchorNode) &&
        editor.isEditable()
      ) {
        const domRange = nativeSelection.getRangeAt(0);
        let rect;
        if (nativeSelection.anchorNode === rootElement) {
          let inner = rootElement;
          while (inner.firstElementChild != null) {
            inner = inner.firstElementChild;
          }
          rect = inner.getBoundingClientRect();
        } else {
          rect = domRange.getBoundingClientRect();
        }
        positionEditorElement(editorElem, rect, anchorElem);
        setLastSelection(selection);
      } else if (!activeElement || activeElement.className !== 'link-input') {
        positionEditorElement(editorElem, null, anchorElem);
        setLastSelection(null);
        setIsLinkEditMode(false);
        setLinkUrl('');
      }
    }
    return true;
  }, [editor, setIsLinkEditMode, anchorElem]);

  useEffect(() => {
    const scrollerElem = anchorElem.parentElement;

    const update = () => {
      editor.getEditorState().read(() => {
        $updateLinkEditor();
      });
    };

    window.addEventListener('resize', update);
    if (scrollerElem) {
      scrollerElem.addEventListener('scroll', update);
    }

    return () => {
      window.removeEventListener('resize', update);
      if (scrollerElem) {
        scrollerElem.removeEventListener('scroll', update);
      }
    };
  }, [anchorElem.parentElement, editor, $updateLinkEditor]);

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          $updateLinkEditor();
        });
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          $updateLinkEditor();
          return true;
        },
        COMMAND_PRIORITY_LOW
      ),
      editor.registerCommand(
        KEY_ESCAPE_COMMAND,
        () => {
          if (isLink) {
            setIsLink(false);
            return true;
          }
          return false;
        },
        COMMAND_PRIORITY_HIGH
      )
    );
  }, [editor, $updateLinkEditor, isLink, setIsLink]);

  useEffect(() => {
    editor.getEditorState().read(() => {
      $updateLinkEditor();
    });
  }, [editor, $updateLinkEditor]);

  useEffect(() => {
    if (isLinkEditMode && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isLinkEditMode, isLink]);

  const monitorInputInteraction = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleLinkSubmission();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      setIsLinkEditMode(false);
    }
  };

  const handleLinkSubmission = () => {
    if (lastSelection !== null) {
      if (editedLinkUrl !== '') {
        const url = editedLinkUrl.startsWith('#')
          ? editedLinkUrl
          : sanitizeUrl(editedLinkUrl);
        editor.dispatchCommand(TOGGLE_LINK_COMMAND, url);
        editor.update(() => {
          const selection = $getSelection();
          if ($isRangeSelection(selection)) {
            const parent = getSelectedNode(selection).getParent();
            if ($isAutoLinkNode(parent)) {
              const linkNode = $createLinkNode(parent.getURL(), {
                rel: parent.__rel,
                target: parent.__target,
                title: parent.__title,
              });
              parent.replace(linkNode, true);
            }
          }
        });
      }
      setEditedLinkUrl('');
      setIsLinkEditMode(false);
    }
  };

  const handleDeleteLink = () => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        const node = getSelectedNode(selection);
        const linkNode = $isLinkNode(node) ? node : ($isLinkNode(node.getParent()) ? node.getParent() : null);
        if (linkNode) {
          const children = linkNode.getChildren();
          for (const child of children) {
            linkNode.insertBefore(child);
          }
          linkNode.remove();
        }
      }
    });
  };

  return (
    <div ref={editorRef} className="link-editor">
      {!isLink ? null : isLinkEditMode ? (
        <>
          <input
            ref={inputRef}
            className="link-input"
            value={editedLinkUrl}
            placeholder="https://example.com"
            onChange={(event) => setEditedLinkUrl(event.target.value)}
            onKeyDown={monitorInputInteraction}
          />
          <div>
            <Tooltip title="Confirm" arrow placement="top">
              <IconButton
                size="small"
                sx={{ color: '#232536', '&:hover': { color: '#3a3e5a' } }}
                onMouseDown={(event) => event.preventDefault()}
                onClick={handleLinkSubmission}
              >
                <Check fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Cancel" arrow placement="top">
              <IconButton
                size="small"
                sx={{ color: '#3a3e5a' }}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => setIsLinkEditMode(false)}
              >
                <Close fontSize="small" />
              </IconButton>
            </Tooltip>
          </div>
        </>
      ) : linkUrl ? (
        <div className="link-view">
          {linkUrl.startsWith('#') ? (
            <span style={{ color: '#232536', fontSize: '14px', display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 200 }}>
              {linkUrl}
            </span>
          ) : (
            <a href={sanitizeUrl(linkUrl)} target="_blank" rel="noopener noreferrer">
              {linkUrl}
            </a>
          )}
          <Tooltip title="Edit link" arrow placement="top">
            <IconButton
              size="small"
              sx={{ color: '#232536', '&:hover': { color: '#3a3e5a' } }}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => {
                setEditedLinkUrl(linkUrl);
                setIsLinkEditMode(true);
              }}
            >
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Remove link" arrow placement="top">
            <IconButton
              size="small"
              color="error"
              onMouseDown={(event) => event.preventDefault()}
              onClick={handleDeleteLink}
            >
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
        </div>
      ) : (
        <div className="link-view">
          <span style={{ color: '#6b7280', fontSize: '13px' }}>No URL set</span>
          <Tooltip title="Edit link" arrow placement="top">
            <IconButton
              size="small"
              sx={{ color: '#232536', '&:hover': { color: '#3a3e5a' } }}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => {
                setEditedLinkUrl('https://');
                setIsLinkEditMode(true);
              }}
            >
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Remove link" arrow placement="top">
            <IconButton
              size="small"
              color="error"
              onMouseDown={(event) => event.preventDefault()}
              onClick={handleDeleteLink}
            >
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
        </div>
      )}
    </div>
  );
}

function useFloatingLinkEditorToolbar(editor, anchorElem, isLinkEditMode, setIsLinkEditMode) {
  const [activeEditor, setActiveEditor] = useState(editor);
  const [isLink, setIsLink] = useState(false);

  useEffect(() => {
    function $updateToolbar() {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        const node = getSelectedNode(selection);
        const linkParent = $isLinkNode(node.getParent()) ? node.getParent() : null;
        const autoLinkParent = $isAutoLinkNode(node.getParent()) ? node.getParent() : null;
        if (linkParent != null || autoLinkParent != null || $isLinkNode(node) || $isAutoLinkNode(node)) {
          setIsLink(true);
        } else {
          setIsLink(false);
        }
      }
    }
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          $updateToolbar();
        });
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        (_payload, newEditor) => {
          $updateToolbar();
          setActiveEditor(newEditor);
          return false;
        },
        COMMAND_PRIORITY_CRITICAL
      )
    );
  }, [editor]);

  return isLink
    ? createPortal(
      <FloatingLinkEditor
        editor={activeEditor}
        isLink={isLink}
        anchorElem={anchorElem}
        setIsLink={setIsLink}
        isLinkEditMode={isLinkEditMode}
        setIsLinkEditMode={setIsLinkEditMode}
      />,
      anchorElem
    )
    : null;
}

export default function FloatingLinkEditorPlugin({
  anchorElem = document.body,
  isLinkEditMode,
  setIsLinkEditMode,
}) {
  const [editor] = useLexicalComposerContext();
  return useFloatingLinkEditorToolbar(editor, anchorElem, isLinkEditMode, setIsLinkEditMode);
}
